import { router, usePage } from '@inertiajs/react'
import type { PageProps } from '@/types'
import { LayoutDashboard, Columns3, Search, Monitor, Video } from 'lucide-react'
import UserMenu from './UserMenu'
import TagCloud from '@/components/notes/TagCloud'
import WikiTree from '@/components/wiki/WikiTree'

const navItems = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard },
  { label: 'Boards', href: '/boards', icon: Columns3 },
  { label: 'Landing Pages', href: '/landing_pages', icon: Monitor },
  { label: 'Demos', href: '/demos', icon: Video },
]

export default function Sidebar({ width }: { width: number }) {
  const { sidebar } = usePage<PageProps>().props
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'

  // Extract current note slug from URL
  const currentSlug = pathname.startsWith('/notes/')
    ? pathname.replace('/notes/', '').split('/')[0]
    : undefined

  return (
    <aside style={{ width }} className="flex flex-shrink-0 flex-col border-r border-gray-200 bg-gray-50">
      <div className="p-4">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 1024 1024" fill="currentColor" className="flex-shrink-0">
            <g transform="translate(0,1024) scale(0.1,-0.1)">
              <path d="M6746 9199 c-137 -10 -251 -20 -253 -22 -2 -2 4 -98 12 -213 8 -115 16 -227 17 -250 l3 -40 -411 -406 c-238 -235 -427 -414 -450 -426 -30 -15 -287 -112 -297 -112 -1 0 -3 37 -3 83 -5 329 -238 577 -544 577 -149 0 -267 -48 -372 -151 -188 -184 -235 -526 -108 -780 18 -35 39 -72 47 -81 14 -16 14 -18 -3 -24 -206 -73 -294 -119 -387 -205 -131 -120 -175 -205 -300 -584 -47 -143 -272 -825 -501 -1515 -228 -690 -466 -1413 -530 -1606 l-115 -352 -179 -343 c-99 -189 -279 -534 -401 -767 -140 -268 -225 -442 -232 -474 -11 -49 -8 -137 7 -186 l6 -22 -196 0 -197 0 3 -138 3 -137 3751 -3 3752 -2 4 122 c2 68 2 131 0 141 -4 16 -59 17 -969 17 -915 0 -965 1 -958 18 4 9 64 163 134 342 l126 325 5 2340 5 2340 56 56 c48 47 59 64 69 112 27 120 -27 291 -107 335 l-40 22 -7 103 c-4 56 -21 282 -37 502 -54 734 -53 717 -36 726 8 5 30 32 48 61 32 49 34 58 34 138 -1 109 -29 186 -91 245 l-42 42 -9 104 c-8 102 -9 104 -33 105 -14 1 -137 -7 -274 -17z m-131 -1748 c19 -272 34 -495 32 -496 -3 -2 -106 -62 -382 -225 -371 -218 -462 -270 -478 -270 -11 0 -610 411 -640 439 -4 5 180 106 410 226 274 142 434 231 465 259 26 22 160 162 300 309 139 148 254 265 255 260 1 -4 18 -230 38 -502z m178 -5791 c71 -179 131 -333 134 -342 5 -17 -30 -18 -645 -18 l-651 0 17 43 c16 38 17 118 17 957 l0 915 -28 88 c-15 49 -39 111 -52 138 -14 27 -150 215 -302 417 -153 202 -354 469 -448 593 -94 125 -179 243 -190 262 l-19 36 248 663 c137 365 252 667 256 671 4 4 105 -38 226 -94 310 -143 338 -152 449 -147 111 5 115 8 416 201 123 79 271 173 329 210 l105 66 5 -2167 5 -2167 128 -325z m-2431 1793 c330 -333 448 -457 456 -483 6 -19 20 -217 32 -440 11 -223 22 -441 25 -485 3 -44 12 -212 20 -373 11 -211 19 -303 30 -329 8 -20 14 -38 12 -40 -2 -2 -558 -2 -1236 -1 l-1232 3 79 130 c44 72 234 389 423 705 356 595 353 589 750 1398 98 199 182 362 186 362 5 0 210 -201 455 -447z"/>
            </g>
          </svg>
          Mission Control
        </h1>
      </div>

      <nav className="space-y-1 px-2">
        {navItems.map(item => {
          const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href))
          return (
            <button
              key={item.href}
              onClick={() => router.visit(item.href)}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                isActive
                  ? 'bg-gray-200 text-gray-900'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </button>
          )
        })}
        <button
          onClick={() => {
            document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))
          }}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <Search className="h-4 w-4" />
          Search
          <kbd className="ml-auto text-[10px] text-gray-400 bg-gray-200 rounded px-1.5 py-0.5">⌘K</kbd>
        </button>
      </nav>

      <div className="mt-4 flex-1 overflow-hidden flex flex-col px-2">
        {sidebar?.tree && (
          <WikiTree tree={sidebar.tree} currentSlug={currentSlug} />
        )}

        {sidebar && sidebar.tags.length > 0 && (
          <TagCloud tags={sidebar.tags} />
        )}
      </div>

      <div className="border-t border-gray-200 p-3">
        <UserMenu />
      </div>
    </aside>
  )
}
